import React, { useContext, useEffect, useState } from 'react';
import { SiteContext } from '../../../../context/SiteContext';
import { useNavigate, useOutletContext } from 'react-router-dom';
import { PageAbsolutePath } from '../../../../consts/Common';
import { ButtonContinue } from '../../../common/Button/ButtonContinue';
import Table, { HeaderRow, TableRow } from '../../../common/Widgets/Table';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faCheck } from '@fortawesome/free-solid-svg-icons/faCheck';
import { faXmark } from '@fortawesome/free-solid-svg-icons/faXmark';
import List from '../../../common/Widgets/List';
import Image from '../../../common/Widgets/Image';
import CardSection from '../../../common/Widgets/CardSection';
import { CardProps } from '../../../common/Widgets/Card';
import MobiliseSecurityDialog from '../MobiliseSecurityDialog';
import MobiliseWelcomeDialog from '../MobiliseWelcomeDialog';
import {
  InsuranceJourneyContext,
  InsuranceJourneyState
} from '../../../common/CommonTypes';
import useCarInsuranceController from '../../../../hooks/insuranceJourney/useCarInsuranceController';
import { getStartQuotePath } from '../InsuranceMethods';
import { useErrorBoundary } from 'react-error-boundary';

const RenaultLandingPage = (): JSX.Element => {
  const [showModalDialog, setShowModalDialog] = useState(false);

  const [showWelcomeDialog, setShowWelcomeDialog] = useState(false);

  const navigate = useNavigate();
  const { showBoundary } = useErrorBoundary();

  const { site } = useContext(SiteContext);

  const { isInsuranceMobilise, brand } = site;

  const phoneNumber = brand.brandPhoneNumbers.quotesAndRenewalsNumber;

  const { pageEvents } = useOutletContext<InsuranceJourneyContext>();

  const { commonEvents } = pageEvents;

  const { setQuote } = commonEvents;

  const { getNewQuote } = useCarInsuranceController();

  const getNewQuoteSuccess = (quote: InsuranceJourneyState) => {
    setQuote({
      ...quote,
      tiaProductCode: site.brand.tiaProductCode
    });

    navigate(
      getStartQuotePath(
        site.user.isDealer ?? false,
        site.brand.useAlternateEligibility
      )
    );
  };

  const onBackendFailure = (err: string) => {
    showBoundary(new Error(err));
  };

  const getStarted = () => {
    getNewQuote(
      site.brand.brandName,
      site.user?.insuranceDealerCode ?? '',
      site.user.token,
      getNewQuoteSuccess,
      onBackendFailure
    );
  };

  const renaultTableHeader: HeaderRow = {
    columns: ['Renault Guaranteed', 'Renault Insurance', 'Top UK insurers']
  };

  const tickComponent = <FontAwesomeIcon icon={faCheck} />;

  const crossComponent = <FontAwesomeIcon icon={faXmark} />;

  const renaultTableRows: TableRow[] = [
    { columns: ['Renault genuine parts', tickComponent, crossComponent] },
    {
      columns: ['Renault approved repairers', tickComponent, crossComponent]
    },
    { columns: ['Renault trained technicians', tickComponent, crossComponent] },
    {
      columns: [
        'Renault manufacturer warranty and safety rating preserved by only fitting Renault genuine parts',
        tickComponent,
        crossComponent
      ]
    }
  ];

  const standardCoverItems: string[] = [
    'Courtesy car (subject to availability)',
    'Comprehensive UK and EU cover',
    '24-hour accident recovery',
    'Windscreen damage cover with no loss of your no claims discount',
    'Cover for replacement locks and in-car personal belongings',
    'Medical cover',
    'Personal accident cover',
    '5 year repair guarantee',
    'A brand new Renault if yours is written off or stolen within 12 months of its new registration date'
  ];

  const extraCoverItems: string[] = [
    '£100,000 legal expenses',
    'No-claim discount protection',
    'Upgrade your courtesy car and stay mobile if your car is written-off or stolen'
  ];

  const whyCards: CardProps[] = [
    {
      id: 'why1',
      altText: 'renault card image 1',
      source: './brands/renault/renault_landing_page_card_01.jpg',
      title: 'genuine parts',
      description:
        'Guaranteed Renault Genuine Parts to keep your Renault 100% Renault'
    },
    {
      id: 'why2',
      altText: 'renault card image 2',
      source: './brands/renault/renault_landing_page_card_02.jpg',
      title: 'trained technicians',
      description:
        'Repairs are carried out using the latest Renault techniques and equipment'
    },
    {
      id: 'why3',
      altText: 'renault card image 3',
      source: './brands/renault/renault_landing_page_card_03.jpg',
      title: 'approved repairs',
      description:
        "Repairs carried out at Renault Approved Bodyshops and guaranteed to meet Renault standards, which isn't the case with some other insurers"
    },
    {
      id: 'why4',
      altText: 'renault card image 4',
      source: './brands/renault/renault_landing_page_card_04.jpg',
      title: 'preserved warranty',
      description:
        'Your car stays protected and its safety rating warranty and resale value is maintained'
    }
  ];

  const onCloseModal = () => {
    setShowModalDialog(false);
  };

  const onContinueModal = () => {
    onCloseModal();
  };

  const onShowModal = () => {
    setShowModalDialog(true);
  };

  const getStartedRetrieveQuote = () => {
    navigate(PageAbsolutePath.Insurance.RetrieveQuote);
  };

  const renderLoginButtons = () => {
    if (isInsuranceMobilise) {
      return (
        <div className='flex-row login-buttons'>
          <ButtonContinue
            name='btnCompleteQuote'
            text='complete quote'
            onClick={onShowModal}
            additionalClassNames='btn-start'
          />
        </div>
      );
    } else {
      return (
        <div className='flex-row login-buttons'>
          <ButtonContinue
            name='btnGetStarted'
            text='start quote'
            onClick={getStarted}
            additionalClassNames='btn-start'
          />
          <ButtonContinue
            name='btnRetrieveQuote'
            text='retrieve quote'
            onClick={getStartedRetrieveQuote}
            additionalClassNames='btn-retrieve'
          />
        </div>
      );
    }
  };

  const renderContinueButton = () => {
    if (isInsuranceMobilise) {
      return (
        <ButtonContinue
          name='btnCompleteQuote2'
          text='complete quote'
          onClick={onShowModal}
          additionalClassNames='btn-start'
        />
      );
    } else {
      return (
        <ButtonContinue
          name='btnGetStarted2'
          text='Start quote'
          onClick={getStarted}
          additionalClassNames='btn-start'
        />
      );
    }
  };

  const onCloseWelcomeDialog = () => {
    setShowWelcomeDialog(false);
  };

  useEffect(() => {
    setShowWelcomeDialog(isInsuranceMobilise);
  }, []);

  return (
    <div className='insurance-landing-page primary-section'>
      <div className='hero-image'>
        <div className='image-text'>
          <h1 className='main-header'>Welcome to Renault Insurance</h1>
          {renderLoginButtons()}
          <h2 className='mobile-hide-section'>
            Or call{' '}
            <a className='calltext-big' href={`tel:${phoneNumber}`}>
              {phoneNumber}
            </a>
          </h2>
        </div>
      </div>
      <div className='margin-panel justify-center flex-row'>
        <CardSection id='whychooseus' title='Why choose us' cards={whyCards} />
      </div>
      <div className='flex-row grey justify-center secondary-section'>
        <div className='margin-panel'>
          <h3>Think all insurers are the same? Think again</h3>
          <p className='second-font'>
            Renault Insurance offers a range of special features to ensure that
            you and your family are in the safest possible hands. You have the
            reassurance of knowing that your Renault will be restored back to
            its pre-accident condition using only genuine Renault parts -
            something that other insurers don't always guarantee. We'll get you
            back on the road safe in the knowledge that your Renault warranty is
            preserved.
          </p>
          <p className='second-font'>
            Our exclusive repair benefits guarantee what many insurers don't:
          </p>
          <Table
            id='landing-page-table'
            header={renaultTableHeader}
            rows={renaultTableRows}
            addendum='Source: Research of UK motor insurer policy wordings, March 2024.'
          />
          <div>
            <div className='flex-row justify-center'>
              {renderContinueButton()}
            </div>
          </div>
        </div>
      </div>
      <div className='tertiary-section'>
        <div className='flex-row lists margin-panel'>
          <div className='two-columns desktop-hide-section '>
            <List
              id='standard-cover'
              title='Standard Cover'
              listItems={standardCoverItems}
            />
          </div>
          <div className='two-columns'>
            <Image
              id='standard-cover-image'
              source='./brands/renault/renault_landing_page_list_01.jpg'
              altText='list-image-1'
            />
          </div>
          <div className='two-columns mobile-hide-section '>
            <List
              id='standard-cover'
              title='Standard Cover'
              listItems={standardCoverItems}
            />
          </div>
        </div>
        <div className='flex-row lists margin-panel'>
          <div className='two-columns'>
            <List
              id='extra-cover'
              title='Optional Extra Cover'
              listItems={extraCoverItems}
            />
          </div>
          <div className='two-columns'>
            <Image
              id='optional-extra-image'
              source='./brands/renault/renault_landing_page_list_02.jpg'
              altText='list-image-2'
            />
          </div>
        </div>
      </div>
      <div className='quaternary-section'>
        <div className='justify-center margin-panel'>
          <div className='justify-center footnote-big'>
            Get the Renault treatment
          </div>
          <div className='justify-center'>
            <div className='flex-row justify-center'>
              {renderContinueButton()}
            </div>
          </div>
        </div>
      </div>
      <MobiliseSecurityDialog
        showDialog={showModalDialog}
        onCloseModal={onCloseModal}
        onContinueModal={onContinueModal}
        cultureCode={site.cultureCode}
        brand={brand.brandName}
        phoneNumber={phoneNumber}
      />
      <MobiliseWelcomeDialog
        showDialog={showWelcomeDialog}
        onCloseModal={onCloseWelcomeDialog}
        brand={brand.brandName}
      />
    </div>
  );
};

export default RenaultLandingPage;
